import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { AccentBar } from './AccentBar';

/**
 * City/lifestyle overview — a centered heading followed by a single centered
 * resident profile (title + richtext bullet list).
 */

export function LifestyleCommunity() {
  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const itemRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="w-full py-14 md:py-20 bg-[var(--vibe-color-primary)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headingRef} className="reveal-fade-up text-center mb-8 md:mb-10">
          <RichTextContent
            path="lifestylecommunity.eyebrow"
            label="Section Eyebrow"
            defaultValue="<p>The Area</p>"
            as="p"
            className="font-body text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[var(--vibe-color-text-on-primary)] opacity-70 mb-3"
          />
          <RichTextContent
            path="lifestylecommunity.title"
            label="Section Title"
            defaultValue="<p>San Tan Valley, Arizona<br>is known as the heart of the:</p>"
            as="h2"
            className="max-w-2xl mx-auto text-balance font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-[var(--vibe-color-text-on-primary)] leading-[1.1] tracking-tight [&_p]:m-0"
          />
          <AccentBar className="mt-5 bg-[var(--vibe-color-text-on-primary)] opacity-60" />
        </div>

        <div ref={itemRef} className="reveal-fade-up max-w-3xl mx-auto">
          <RichTextContent
            path="lifestylecommunity.item1_title"
            label="Item Title"
            defaultValue="<p>Oil &amp; Gas Professionals and Families</p>"
            as="h3"
            className="font-heading text-lg sm:text-xl font-normal text-[var(--vibe-color-text-on-primary)] tracking-tight text-center mb-3"
          />
          <RichTextContent
            path="lifestylecommunity.item1_body"
            label="Item Body"
            defaultValue="<ul><li>Many residents work in the energy industry, including engineers, geologists, rig workers, and executives.</li><li>These families often experience rapid booms and occasional busts, depending on oil prices.</li><li>Neighborhoods like Grassland Estates and parts of north Midland cater to professionals looking for newer, family-oriented homes.</li></ul>"
            as="div"
            className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] opacity-90 [&_a]:underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_p]:mb-2 last:[&_p]:mb-0"
          />
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleCommunity',
  displayName: 'Lifestyle Community',
  tokens: {
    'lifestylecommunity.eyebrow': {
      label: 'Section Eyebrow',
      type: 'richtext',
      defaultValue: '<p>The Area</p>',
    },
    'lifestylecommunity.title': {
      label: 'Section Title',
      type: 'richtext',
      defaultValue: '<p>San Tan Valley, Arizona<br>is known as the heart of the:</p>',
    },
    'lifestylecommunity.item1_title': {
      label: 'Item Title',
      type: 'richtext',
      defaultValue: '<p>Oil &amp; Gas Professionals and Families</p>',
    },
    'lifestylecommunity.item1_body': {
      label: 'Item Body',
      type: 'richtext',
      defaultValue:
        '<ul><li>Many residents work in the energy industry, including engineers, geologists, rig workers, and executives.</li><li>These families often experience rapid booms and occasional busts, depending on oil prices.</li><li>Neighborhoods like Grassland Estates and parts of north Midland cater to professionals looking for newer, family-oriented homes.</li></ul>',
    },
  },
};

(LifestyleCommunity as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LifestyleCommunity;
